import { Box, Chip, LinearProgress, Unstable_Grid2 as Grid, Typography } from "@mui/material";
import { Favorite, Troubleshoot, Hub, Support, Schedule } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { PreferredDaySchedule, PreferredSchedule, PREFERRED_SCHEDULE_SESSIONS_COUNT, ServicePeriodicity, SessionsSummary } from "../../api/client-sessions-summary";
import { WarningBox } from "../../components/warnings/WarningBox";
import { clientDebtWarnings } from "./ClientDebtWarning";
import { treatmentProvidedAppointmentsWarning } from "../treatments/treatment-prescription/TreatmentPrescriptionWarning";

type ClientSessionsSummarySections = {
  donePaid?: boolean;
  scheduledPrescribed?: boolean;
  warnings?: boolean;
  preferredSchedule?: boolean;
};

type ClientSessionsSummaryProps = {
  sessions: SessionsSummary | undefined;
  treatmentId: number | null;
  options: { flexDirection: "row" | "column", presentation: "sessionPreview" | "header" };
  isLoading?: boolean;
  sections?: ClientSessionsSummarySections;
};

const defaultSections: ClientSessionsSummarySections = {
  donePaid: true,
  scheduledPrescribed: true,
  warnings: true,
  preferredSchedule: false,
};

export const ClientSessionsSummary = ({ sessions, treatmentId, options, isLoading, sections = defaultSections }: ClientSessionsSummaryProps) => {
  const visibleSections = { ...defaultSections, ...sections };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box flexDirection={options.flexDirection || "row"}>
      <Grid container columnSpacing={6} rowSpacing={1} justifyItems="flex-start" display="flex" flexWrap="wrap" flexDirection={options.flexDirection} ml={1}>
        <Grid xs={12} md={options.presentation === "sessionPreview" ? 10 : 6}>
          {visibleSections.donePaid && <DonePaidSection sessions={sessions} />}
        </Grid>
        <Grid xs={12} md={options.presentation === "sessionPreview" ? 12 : 6}>

          {visibleSections.scheduledPrescribed && <ScheduledPrescribedSection sessions={sessions} isSessionPreview={options.presentation === "sessionPreview"} />}
        </Grid>
          {visibleSections.preferredSchedule && sessions?.preferredSchedule && <PreferredScheduleSection preferredSchedule={sessions.preferredSchedule} />}
          {visibleSections.warnings && sessions && <WarningsSection sessions={sessions} treatmentId={treatmentId} options={options} />}
      </Grid>
    </Box>
  );
};

type DonePaidSectionProps = {
  sessions: SessionsSummary | undefined;
};

const DonePaidSection = ({ sessions }: DonePaidSectionProps) => {
  return (
    <>

        <Typography variant="h6" color="secondary" mb={2}>
          Realizadas / Pagas
        </Typography>
      <Grid container rowSpacing={1} xs={12} display="flex" alignItems="center" rowGap={1} columnGap={2} justifyContent="flex-start">
      <Grid display="flex" alignItems="flex-start">
        <Hub color="primary" sx={{ mr: 1 }} />
        <Typography variant="body1" color="primary">
          NeuroFeedback:
        </Typography>
        <Typography ml={1} variant="body1">
          {sessions?.nfbDone}/{sessions?.nfbBought}
        </Typography>
      </Grid>
      <Grid display="flex" alignItems="flex-start">
        <Favorite color="primary" sx={{ mr: 1 }} />
        <Typography variant="body1" color="primary">
          Psicologia:
        </Typography>
        <Typography ml={1} variant="body1">
          {sessions?.psyDone}/{sessions?.psyBought}
        </Typography>
      </Grid>
      <Grid display="flex" alignItems="flex-start">
        <Troubleshoot color="primary" sx={{ mr: 1 }} />
        <Typography variant="body1" color="primary">
          Avaliações:
        </Typography>
        <Typography ml={1} variant="body1">
          {sessions?.evaluationsDone}/{sessions?.evaluationsBought}
        </Typography>
      </Grid>
      <Grid display="flex" alignItems="flex-start">
        <Support color="primary" sx={{ mr: 1 }} />
        <Typography variant="body1" color="primary">
          Acompanhamento:
        </Typography>
        <Typography ml={1} variant="body1">
          {sessions?.followUpDone}
        </Typography>
      </Grid>
      </Grid>
    </>
  );
};

type ScheduledPrescribedSectionProps = {
  sessions: SessionsSummary | undefined;
  isSessionPreview?: boolean;
};

const ScheduledPrescribedSection = ({ sessions, isSessionPreview }: ScheduledPrescribedSectionProps) => {
  return (
    <>
        <Typography variant="h6" color="secondary" mt={isSessionPreview ? 2 : 0} mb={2}>
          Agendadas / Prescritas
        </Typography>
        <Grid container rowSpacing={1} xs={12} display="flex" alignItems="flex-start" rowGap={1} columnGap={2} justifyContent="flex-start">
      <Grid display="flex" alignItems="flex-start">
        <Hub color="primary" sx={{ mr: 1 }} />
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <Typography variant="body1" color="primary">
              NeuroFeedback:
            </Typography>
            <BookedPrescribedValue booked={sessions?.nfbBooked} prescribed={sessions?.nfbPrescribed} />
          </Box>
          <LatestBookedCaption date={sessions?.nfbLatestBookedDate} />
        </Box>
      </Grid>
      <Grid display="flex" alignItems="flex-start">
        <Favorite color="primary" sx={{ mr: 1 }} />
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <Typography variant="body1" color="primary">
              Psicologia:
            </Typography>
            <BookedPrescribedValue booked={sessions?.psyBooked} prescribed={sessions?.psyPrescribed} />
          </Box>
          <LatestBookedCaption date={sessions?.psyLatestBookedDate} />
        </Box>
      </Grid>
      <Grid display="flex" alignItems="flex-start">
        <Troubleshoot color="primary" sx={{ mr: 2 }} />
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <Typography variant="body1" color="primary">
              Avaliações:
            </Typography>
            <BookedPrescribedValue booked={sessions?.evaluationsBooked} prescribed={sessions?.evaluationsPrescribed} />
          </Box>
          <LatestBookedCaption date={sessions?.evaluationsLatestBookedDate} />
        </Box>
      </Grid>
      </Grid>
    </>
  );
};

type PreferredScheduleSectionProps = {
  preferredSchedule: PreferredSchedule;
};

const formatDayTimes = (day: PreferredDaySchedule) => {
  const sortedTimes = [...day.times].sort((a, b) => b.count - a.count);
  const hasFrequentTime = sortedTimes.some((t) => t.count > 1);
  return (
    <>
      {hasFrequentTime ? <strong>{day.dayName}</strong> : day.dayName}:{" "}
      {sortedTimes.map((t, index) => (
        <span key={t.time}>
          {index > 0 && ", "}
          {t.count > 1 ? <strong>{t.time} ({t.count}x)</strong> : t.time}
        </span>
      ))}
    </>
  );
};

const getDayTotalCount = (day: PreferredDaySchedule) => {
  const total = day.times.reduce((sum, t) => sum + t.count, 0);
  return total;
};

const PERIODICITY_LABELS: Record<string, string> = {
  weekly: "semana",
  biweekly: "quinzena",
  monthly: "mês",
};

const formatPeriodicity = (periodicity: ServicePeriodicity) => {
  if (!periodicity) return null;
  if (periodicity.status === "finished") return "Concluído";
  const periodicityLabel = PERIODICITY_LABELS[periodicity.periodicity] || periodicity.periodicity;
  return `${periodicity.quantity}x/${periodicityLabel}`;
};

type ServicePreferredSlotsProps = {
  icon: React.ReactNode;
  label: string;
  days: Array<PreferredDaySchedule>;
  periodicity: ServicePeriodicity;
};

const ServicePreferredSlots = ({ icon, label, days, periodicity }: ServicePreferredSlotsProps) => {
  if (days.length === 0) return null;

  const periodicityText = formatPeriodicity(periodicity);

  return (
    <Grid display="flex" alignItems="flex-start">
      {icon}
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" color="primary">
            {label}:
          </Typography>
          {periodicityText && (
            <Typography variant="caption" color="text.secondary">
              ({periodicityText})
            </Typography>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {days.map((day: PreferredDaySchedule) => {
            const isFrequent = getDayTotalCount(day) > 1;
            return (
              <Chip
                key={day.dayOfWeek}
                icon={<Schedule />}
                label={formatDayTimes(day)}
                size="small"
                variant={isFrequent ? "filled" : "outlined"}
                sx={{
                  color: "text.secondary",
                  borderColor: "text.secondary",
                  backgroundColor: isFrequent ? "action.selected" : "transparent",
                  "& .MuiChip-icon": { color: "text.secondary" },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Grid>
  );
};

const PreferredScheduleSection = ({ preferredSchedule }: PreferredScheduleSectionProps) => {
  const hasSlots = preferredSchedule.nfb.length > 0 || preferredSchedule.psy.length > 0;

  if (!hasSlots) return null;

  return (
    <>
      <Grid xs={12} mt={2}>
        <Typography variant="h6" color="secondary">
          Horário Preferido
        </Typography>
        <Typography variant="caption" color="text.secondary">
          (últimas {PREFERRED_SCHEDULE_SESSIONS_COUNT} sessões)
        </Typography>
      </Grid>
      <ServicePreferredSlots icon={<Hub color="primary" sx={{ mr: 2 }} />} label="NeuroFeedback" days={preferredSchedule.nfb} periodicity={preferredSchedule.nfbPeriodicity} />
      <ServicePreferredSlots icon={<Favorite color="primary" sx={{ mr: 2 }} />} label="Psicologia" days={preferredSchedule.psy} periodicity={preferredSchedule.psyPeriodicity} />
    </>
  );
};

type BookedPrescribedValueProps = {
  booked: number | undefined;
  prescribed: number | undefined;
};

const BookedPrescribedValue = ({ booked, prescribed }: BookedPrescribedValueProps) => {
  const bookedValue = booked ?? 0;
  const prescribedValue = prescribed ?? 0;
  const isFullyBooked = prescribedValue === 0 || bookedValue >= prescribedValue;
  const warningColor = isFullyBooked ? "text.primary" : "warning.main";

  return (
    <Typography variant="body1" ml={1} color={warningColor} fontWeight={isFullyBooked ? "normal" : "bold"}>
      {bookedValue}/{prescribedValue}
    </Typography>
  );
};

type LatestBookedCaptionProps = {
  date: string | null | undefined;
};

const LatestBookedCaption = ({ date }: LatestBookedCaptionProps) => {
  if (!date) return null;

  const formattedDate = format(parseISO(date), "dd/MM/yyyy HH:mm");
  return (
    <Typography variant="caption" color="text.secondary">
      {formattedDate}
    </Typography>
  );
};

type WarningSectionProps = {
  sessions: SessionsSummary;
  treatmentId: number | null;
  options: { flexDirection: "row" | "column" };
};

const WarningsSection = ({ sessions, treatmentId, options }: WarningSectionProps) => {
  const warningsOfClients = clientDebtWarnings(sessions);
  const treatmentProvidedWarning = treatmentProvidedAppointmentsWarning(treatmentId);

  return (
    <Grid
      xs={12}
      mt={{ xs: 5, sm: options.flexDirection === "column" ? 4 : 0 }}
      ml={{ xs: 0, sm: options.flexDirection === "column" ? 1 : 3 }}
      sx={{
        display: "flex",
        justifyContent: {
          xs: "flex-start",
          xl: options.flexDirection === "column" ? "flex-start" : "center",
        },
        transform: options.flexDirection === "column" ? 0 : "translate(0px, 7px)"
      }}
    >
      <WarningBox
        wrap={options.flexDirection === "column"}
        warnings={[warningsOfClients.debtWarning, warningsOfClients.packWarning, treatmentProvidedWarning]}
      />
    </Grid>
  );
};
